import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const useTokenExpiry = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');

  const handleLogout = useCallback(() => {
    console.log('🚪 Déconnexion...');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userData');
    setShowWarning(false);
    setError('');
    window.location.href = '/login';
  }, []);

  const checkTokenExpiry = useCallback(() => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    const authToken = localStorage.getItem('authToken');

    if (!tokenExpiry || !authToken) {
      setShowWarning(false);
      return;
    }

    const now = new Date().getTime();
    const expiryTime = parseInt(tokenExpiry);
    const timeRemaining = expiryTime - now;

    if (timeRemaining > 0 && timeRemaining <= 5 * 60 * 1000) {
      setShowWarning(true);
      setTimeLeft(Math.ceil(timeRemaining / 1000));
    } else {
      setShowWarning(false);
    }

    if (timeRemaining <= 0) {
      handleLogout();
    }
  }, [handleLogout]);

  const refreshToken = useCallback(async () => {
    if (isRefreshing) {
      console.log('🔄 Rafraîchissement déjà en cours...');
      return false;
    }

    setIsRefreshing(true);
    setError('');
    console.log('🔄 Début du rafraîchissement du token...');

    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      const authToken = localStorage.getItem('authToken');
      
      if (!refreshTokenValue) {
        throw new Error('Aucun refresh token disponible');
      }

      console.log('📡 Envoi de la requête de rafraîchissement...');
      console.log('🔗 Endpoint: /refresh-token');
      console.log('🔑 Refresh token présent:', !!refreshTokenValue);

      // ESSAI 1: Format standard avec header Authorization
      let config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        timeout: 10000
      };

      // Ajouter le token d'authentification actuel si disponible
      if (authToken) {
        config.headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.post('/refresh-token', {
        refresh_token: refreshTokenValue
        // Alternative: selon l'API de votre serveur
        // token: refreshTokenValue
        // refreshToken: refreshTokenValue
      }, config);

      const data = response.data;
      console.log('✅ Réponse de rafraîîchissement reçue:', data);

      if (!data.access_token) {
        throw new Error('Token d\'accès manquant dans la réponse');
      }

      // Mettre à jour les tokens
      localStorage.setItem('authToken', data.access_token);
      
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      
      const expiresIn = data.expires_in || 3600;
      const newExpiry = new Date().getTime() + (expiresIn * 1000);
      localStorage.setItem('tokenExpiry', newExpiry.toString());

      setShowWarning(false);
      setIsRefreshing(false);
      setError('');
      
      console.log('🎯 Token rafraîchi avec succès!');
      console.log('⏰ Nouvelle expiration:', new Date(newExpiry));
      return true;

    } catch (error) {
      setIsRefreshing(false);
      console.error('❌ Erreur lors du rafraîchissement:', error);
      
      let errorMessage = 'Erreur lors du rafraîchissement de la session';
      
      if (error.response) {
        console.error('📡 Status HTTP:', error.response.status);
        console.error('📦 Données de l\'erreur:', error.response.data);
        
        if (error.response.status === 401) {
          console.log('🔐 Refresh token invalide ou expiré');
          errorMessage = 'Votre session a expiré, veuillez vous reconnecter';
          setTimeout(handleLogout, 2000);
        } else if (error.response.status === 404) {
          console.log('🔍 Endpoint /refresh-token non trouvé');
          errorMessage = 'Service temporairement indisponible';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data.message || 'Requête invalide';
        }
      } else if (error.request) {
        console.error('🌐 Aucune réponse du serveur');
        errorMessage = 'Problème de connexion au serveur';
      } else {
        console.error('💥 Erreur inattendue:', error.message);
        errorMessage = error.message || 'Erreur inattendue';
      }
      
      setError(errorMessage);
      return false;
    }
  }, [isRefreshing, handleLogout]);

  useEffect(() => {
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 30000);
    return () => clearInterval(interval);
  }, [checkTokenExpiry]);

  return {
    showWarning,
    setShowWarning,
    timeLeft,
    refreshToken,
    handleLogout,
    isRefreshing,
    error,
    setError
  };
};